/**
 * Data Sanitization Utility
 * Prevents c_name errors by cleaning data before it reaches components
 */

// Sanitize category data recursively
export const sanitizeCategoryData = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }

  return categories
    .filter(cat => cat && typeof cat === 'object')
    .map(cat => {
      // Ensure c_name exists and is a string
      if (!cat.c_name || typeof cat.c_name !== 'string') {
        console.warn('Invalid category data found:', cat);
        return null;
      }

      // Create a new object to avoid mutation
      const sanitizedCat = { ...cat };

      // Recursively sanitize children
      if (sanitizedCat.children && Array.isArray(sanitizedCat.children)) {
        sanitizedCat.children = sanitizeCategoryData(sanitizedCat.children);
      }

      return sanitizedCat;
    })
    .filter(cat => cat !== null);
};

// Safe c_name accessor
export const safeGetCName = (item, fallback = 'Unknown Category') => {
  if (!item) return fallback;
  if (typeof item === 'string') return item;
  if (typeof item === 'object' && item.c_name) {
    return typeof item.c_name === 'string' ? item.c_name : fallback;
  }
  return fallback;
};

// Safe category array accessor
export const safeGetCategories = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }
  return sanitizeCategoryData(categories);
};

// Safe category list rendering
export const safeRenderCategoryList = (categories, renderFunction) => {
  const safeCategories = safeGetCategories(categories);
  
  return safeCategories.map((cat, index) => {
    try {
      return renderFunction(cat, index);
    } catch (error) {
      console.error('Error rendering category list item:', error);
      return null;
    }
  }).filter(item => item !== null);
};

// Safe category mapping
export const safeMapCategories = (categories, callback) => {
  const safeCategories = safeGetCategories(categories);
  return safeCategories.map((cat, index) => {
    try {
      return callback(cat, index);
    } catch (error) {
      console.error('Error in category mapping:', error);
      return null;
    }
  }).filter(item => item !== null);
};

// Safe category filtering
export const safeFilterCategories = (categories, predicate) => {
  const safeCategories = safeGetCategories(categories);
  return safeCategories.filter((cat, index) => {
    try {
      return predicate(cat, index);
    } catch (error) {
      console.error('Error in category filtering:', error);
      return false;
    }
  });
};

// Safe category find
export const safeFindCategory = (categories, predicate) => {
  const safeCategories = safeGetCategories(categories);
  try {
    return safeCategories.find(predicate) || null;
  } catch (error) {
    console.error('Error in category find:', error);
    return null;
  }
};

// Safe category path building
export const safeBuildCategoryPath = (categoryPath) => {
  if (!categoryPath || !Array.isArray(categoryPath)) {
    return '';
  }
  
  return categoryPath
    .map(item => safeGetCName(item))
    .filter(name => name && name !== 'Unknown Category')
    .join('/');
};

// Safe category name extraction
export const safeExtractCategoryName = (item) => {
  if (!item) return 'Unknown Category';
  
  // Try multiple possible field names
  const possibleNames = ['c_name', 'name', 'tagName', 'title', 'categoryName'];
  
  for (const field of possibleNames) {
    if (item[field] && typeof item[field] === 'string') {
      return item[field];
    }
  }
  
  return 'Unknown Category';
};

// Global category data interceptor
export const interceptCategoryData = (data) => {
  if (!data) return [];
  
  // If it's already an array, sanitize it
  if (Array.isArray(data)) {
    return sanitizeCategoryData(data);
  }
  
  // If it's an object with categories property
  if (typeof data === 'object' && data.categories) {
    return sanitizeCategoryData(data.categories);
  }
  
  // If it's an object with data property
  if (typeof data === 'object' && data.data) {
    return sanitizeCategoryData(data.data);
  }
  
  return [];
};

// Safe category rendering wrapper
export const safeRenderCategory = (item, renderFunction) => {
  if (!item || !item.c_name) {
    return null;
  }
  
  try {
    return renderFunction(item);
  } catch (error) {
    console.error('Error rendering category:', error);
    return null;
  }
};

export default {
  sanitizeCategoryData,
  safeGetCName,
  safeGetCategories,
  safeMapCategories,
  safeFilterCategories,
  safeFindCategory,
  safeBuildCategoryPath,
  safeExtractCategoryName,
  interceptCategoryData,
  safeRenderCategory,
  safeRenderCategoryList
};
